import { useMemo } from 'react';

import {
  Card,
  NoData,
  Skeleton,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import {
  WorkloadReliabilitySummariesResponse,
  WorkloadReliabilitySummariesResponseItem,
} from '@cast/types';
import { dashesOrNum, formatPercentage, toNumOrZero } from '@cast/utils';

import { formatLatencyWithUnit } from 'features/cluster/_components/reliability/_utils/formatLatency';

import { UnhealthyWorkloadsCard } from './UnhealthyWorkloadsCard';
import { WorkloadSummaryListRows } from './WorkloadSummaryListRows';

type RankedSummary = {
  key: 'highestLatencyP95' | 'highestErrorRate' | 'highestErrorRatePercent';
  title: string;
  formatMetric: (metric?: number) => string;
};

const rankedSummaries: RankedSummary[] = [
  {
    key: 'highestLatencyP95',
    title: 'Worst P95 Latency',
    formatMetric: (metric) =>
      formatLatencyWithUnit(
        metric !== undefined ? metric * 1000 : undefined,
        2
      ),
  },
  {
    key: 'highestErrorRate',
    title: 'Worst errors (total)',
    formatMetric: (metric) =>
      `${dashesOrNum({ value: metric, roundBy: 2 })} req/s`,
  },
  {
    key: 'highestErrorRatePercent',
    title: 'Worst errors (%)',
    formatMetric: (metric) => formatPercentage(metric),
  },
];

type RankedCardProps = {
  title: string;
  items: WorkloadReliabilitySummariesResponseItem[];
  formatMetric: (metric?: number) => string;
  isLoading: boolean;
};

const RankedCard = ({
  title,
  items,
  formatMetric,
  isLoading,
}: RankedCardProps) => {
  const rankedItems = items.slice(0, 5);

  return (
    <Card sx={{ p: 12 }}>
      <Stack gap={8}>
        <Typography variant="L12R">{title}</Typography>
        {isLoading ? (
          <Skeleton height={120} />
        ) : (
          <Stack gap={8}>
            {rankedItems.length ? (
              <WorkloadSummaryListRows
                items={rankedItems}
                formatMetric={formatMetric}
              />
            ) : (
              <NoData
                testId="ranked-card-no-data"
                hexProps={{ size: 40 }}
                stackProps={{ py: 8 }}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

type ReliabilitySummariesSectionProps = {
  data?: WorkloadReliabilitySummariesResponse;
  isLoading: boolean;
};

const UNHEALTHY_ERROR_RATE_THRESHOLD = 5;

export const ReliabilitySummariesSection = ({
  data,
  isLoading,
}: ReliabilitySummariesSectionProps) => {
  const unhealthyWorkloads = data?.summary?.unhealthyWorkloads || '0';

  const itemsByKey = useMemo(
    () => ({
      highestLatencyP95: data?.highestLatencyP95 || [],
      highestErrorRate: data?.highestErrorRate || [],
      highestErrorRatePercent: data?.highestErrorRatePercent || [],
    }),
    [data]
  );

  const unhealthyItems = useMemo(
    () =>
      itemsByKey.highestErrorRatePercent.filter(
        (item) => (item.metric ?? 0) > UNHEALTHY_ERROR_RATE_THRESHOLD
      ),
    [itemsByKey.highestErrorRatePercent]
  );

  const moreCount = Math.max(
    0,
    toNumOrZero(unhealthyWorkloads) - unhealthyItems.length
  );

  return (
    <Box data-testid="workloads-reliability-summaries-section" mb={15}>
      <UnhealthyWorkloadsCard
        items={unhealthyItems}
        totalCount={unhealthyWorkloads}
        moreCount={moreCount}
        isLoading={isLoading}
      />
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
        gap={12}
      >
        {rankedSummaries.map((summary) => (
          <Box key={summary.key} sx={{ minWidth: 0 }}>
            <RankedCard
              title={summary.title}
              items={itemsByKey[summary.key]}
              formatMetric={summary.formatMetric}
              isLoading={isLoading}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
